/**
 * Scroll position after a page reload (or a back/forward visit from another site).
 *
 * The browser restores the scroll position as soon as the page loads, but our pages fetch their data afterwards,
 * so at that moment the page is too short and it ends up jumping. We save the position when the page is left
 * and scroll back ourselves once the data has loaded.
 */
import { useEffect, useRef } from 'react';
import { useIsFetching } from '@tanstack/react-query';

const KEY = 'bt.scroll:';
const SETTLE_MS = 150;
const MAX_WAIT_MS = 5000;

const pageKey = () => KEY + location.pathname + location.search;

function navigationType(): string | undefined {
  try {
    return (performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined)?.type;
  } catch {
    return undefined;
  }
}

/** Position to restore, read once when the app starts. */
let pending: number | null = null;
if (navigationType() === 'reload' || navigationType() === 'back_forward') {
  try {
    const y = Number(sessionStorage.getItem(pageKey()));
    if (y > 0) {
      pending = y;
      // only this history entry: in-app back/forward keeps the browser's own restoration
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    }
  } catch {
    /* storage unavailable */
  }
}

export function useReloadScrollRestore() {
  const fetching = useIsFetching();
  const started = useRef(Date.now());

  useEffect(() => {
    const save = () => {
      try {
        sessionStorage.setItem(pageKey(), String(Math.round(window.scrollY)));
      } catch {
        /* storage unavailable */
      }
    };
    // someone who already scrolls by themselves is not moved
    const cancel = () => finish(null);
    window.addEventListener('pagehide', save);
    const userInput = ['wheel', 'touchstart', 'keydown', 'mousedown'] as const;
    if (pending !== null) for (const e of userInput) window.addEventListener(e, cancel, { once: true, passive: true });
    return () => {
      window.removeEventListener('pagehide', save);
      for (const e of userInput) window.removeEventListener(e, cancel);
    };
  }, []);

  useEffect(() => {
    if (pending === null) return;
    // restore once nothing has been loading for a moment, or after a few seconds at most
    const delay = fetching > 0 ? Math.max(0, MAX_WAIT_MS - (Date.now() - started.current)) : SETTLE_MS;
    const timer = window.setTimeout(() => finish(pending), delay);
    return () => window.clearTimeout(timer);
  }, [fetching]);
}

function finish(y: number | null) {
  if (pending === null) return;
  pending = null;
  if (y !== null) window.scrollTo(0, y);
  if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
}
